import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Pressable,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import theme from '../../theme';

interface DropdownMenuItem {
  id: string;
  label: string;
  icon: string;
  color?: string;
  onPress: () => void;
}

interface DropdownMenuProps {
  items: DropdownMenuItem[];
  triggerComponent?: React.ReactNode;
  disabled?: boolean;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  items,
  triggerComponent,
  disabled = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<TouchableOpacity>(null);

  const showDropdown = () => {
    if (disabled) return;
    
    triggerRef.current?.measure((fx, fy, width, height, px, py) => {
      const screenWidth = Dimensions.get('window').width;
      const menuWidth = 150;
      
      // Calculate position - show to the left if close to right edge
      const x = px + width > screenWidth - menuWidth ? px - menuWidth + width : px;
      
      setPosition({
        x: Math.max(10, x), // Ensure minimum padding from screen edge
        y: py + height + 5, // Add small gap below trigger
      });
      setIsVisible(true);
    });
  };

  const hideDropdown = () => {
    setIsVisible(false);
  };

  const handleItemPress = (item: DropdownMenuItem) => {
    hideDropdown();
    setTimeout(() => {
      item.onPress();
    }, 100); // Small delay to allow menu to close smoothly
  };

  const defaultTrigger = (
    <View style={styles.defaultTriggerContainer}>
      <View style={styles.defaultTriggerInner}>
        <Ionicons 
          name="ellipsis-horizontal" 
          size={16} 
          color={disabled ? theme.colors.gray300 : theme.colors.textPrimary} 
        />
      </View>
    </View>
  );

  return (
    <>
      {triggerComponent ? (
        <TouchableOpacity
          ref={triggerRef}
          onPress={showDropdown}
          disabled={disabled}
        >
          {triggerComponent}
        </TouchableOpacity>
      ) : (
        <TouchableOpacity
          ref={triggerRef}
          onPress={showDropdown}
          disabled={disabled}
        >
          {defaultTrigger}
        </TouchableOpacity>
      )}

      <Modal
        visible={isVisible}
        transparent
        animationType="fade"
        onRequestClose={hideDropdown}
      >
        <Pressable style={styles.overlay} onPress={hideDropdown}>
          <View
            style={[
              styles.dropdown,
              {
                left: position.x,
                top: position.y,
              },
            ]}
          >
            {items.map((item, index) => (
              <TouchableOpacity
                key={item.id}
                style={[
                  styles.dropdownItem,
                  index === items.length - 1 && styles.lastItem,
                ]}
                onPress={() => handleItemPress(item)}
              >
                <Ionicons
                  name={item.icon as any}
                  size={16}
                  color={item.color || theme.colors.textPrimary}
                  style={styles.itemIcon}
                />
                <Text
                  style={[
                    styles.itemText,
                    item.color && { color: item.color },
                  ]}
                >
                  {item.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </Pressable>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  defaultTriggerContainer: {
    padding: 8,
    borderRadius: 4,
  },
  defaultTriggerInner: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
  dropdown: {
    position: 'absolute',
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    minWidth: 150,
    maxWidth: 200,
    ...theme.shadows.lg,
    elevation: 8,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray100,
  },
  lastItem: {
    borderBottomWidth: 0,
  },
  itemIcon: {
    marginRight: theme.spacing.sm,
    width: 16,
  },
  itemText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textPrimary,
    flex: 1,
  },
});

export default DropdownMenu;
